import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import { darken } from '@mui/system';
import { useTheme } from '@mui/styles';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

import { colors } from 'src/utils/customColors';
import { toMoment } from 'src/utils/datetime';

import messages from '../../messages';

import type { DefaultTheme } from '@mui/styles';

const WEEK_STATS_WIDTH = '14%';
const DAY_CELL_HEIGHT = 90;

const bottomBorderSx = (theme: DefaultTheme) => ({
  borderBottom: `thin solid ${theme.palette.divider}`,
});

const stackSx = (theme: DefaultTheme) => ({
  marginBottom: theme.spacing(0.25),
  padding: theme.spacing(0.25, 0.5),
  width: 'fit-content',
});

const iconButtonStyleSx = (bgColor?: string) => (theme: DefaultTheme) => ({
  width: theme.spacing(3.5),
  height: theme.spacing(1),
  borderRadius: theme.spacing(1),
  backgroundColor: bgColor || theme.palette.error.main,
  '&:hover': {
    backgroundColor: darken(bgColor || theme.palette.error.main, 0.2),
  },
});

const iconStyleSx = (theme: DefaultTheme) => ({
  color: theme.palette.common.white,
  fontSize: theme.spacing(1.25),
});

const iconButtonSx = (theme: DefaultTheme) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.25, 1),
  background: colors.orangeRed,
});

const acceptedButtonSx = (theme: DefaultTheme) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.25, 1),
  border: `thin solid ${theme.palette.primary.main}`,
});

const getJobStatusColor = (
  currDayData: UCM.CalendarJobType | undefined,
  isWaitlisted: boolean,
  theme: DefaultTheme,
) => {
  if (isWaitlisted) {
    return colors.yellow;
  }

  switch (currDayData?.status) {
    case 'applied':
      return colors.yellow;
    case 'accepted':
    case 'booked':
      return colors.lightGreenSecondary;
    case 'rejected':
    case 'unbooked':
    case 'withdrawn':
    case 'cancelled':
      return theme.palette.error.light;
    default:
      return theme.palette.common.white;
  }
};

const canShowApproveButton = (status: string | undefined) => {
  return (
    status === 'unbooked' || status === 'cancelled' || status === 'applied'
  );
};

const canShowRejectButton = (status: string | undefined) => {
  return status === 'booked' || status === 'applied';
};

const DayActionButtons = ({
  onReject,
  onApprove,
  currDayData,
}: {
  currDayData: UCM.CalendarJobType;
  onApprove: (
    event: React.MouseEvent<HTMLElement>,
    data: UCM.CalendarJobType,
  ) => void;
  onReject: (
    event: React.MouseEvent<HTMLElement>,
    data: UCM.CalendarJobType,
  ) => void;
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <Grid container spacing={0.5}>
        {canShowApproveButton(currDayData?.status) && (
          <Grid>
            <IconButton
              size="small"
              sx={iconButtonStyleSx(colors.lightGreen)}
              onClick={(event) => onApprove(event, currDayData)}
            >
              <AddOutlinedIcon sx={iconStyleSx} />
            </IconButton>
          </Grid>
        )}
        {canShowRejectButton(currDayData?.status) && (
          <Grid>
            <IconButton
              size="small"
              sx={iconButtonStyleSx()}
              onClick={(event) => onReject(event, currDayData)}
            >
              <RemoveOutlinedIcon sx={iconStyleSx} />
            </IconButton>
          </Grid>
        )}
      </Grid>

      {currDayData?.status === 'rejected' && (
        <Grid>
          <IconButton
            size="small"
            sx={iconButtonSx}
            onClick={(event) => onApprove(event, currDayData)}
          >
            <Typography fontSize={8} color="common.white" lineHeight="normal">
              {formatMessage(messages.reapplyLabel)}
            </Typography>
          </IconButton>
        </Grid>
      )}

      {currDayData?.status === 'accepted' && (
        <Grid>
          <IconButton
            size="small"
            sx={acceptedButtonSx}
            onClick={(event) => onReject(event, currDayData)}
          >
            <Typography fontSize={8} color="common.black" lineHeight="normal">
              {formatMessage(messages.cancelLabel)}
            </Typography>
          </IconButton>
        </Grid>
      )}
    </>
  );
};

type WeeklyStats = {
  available: number;
  unavailable: number;
};

type WeekStatsProps = {
  weeklyUsersAvailability?: Record<number, WeeklyStats>;
  week?: number;
};

const WeekStats = ({ weeklyUsersAvailability, week }: WeekStatsProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const weeklyAvailable =
    week && weeklyUsersAvailability ? weeklyUsersAvailability[week] : null;

  if (!weeklyAvailable) {
    return (
      <Grid
        flexDirection="column"
        sx={bottomBorderSx}
        width={WEEK_STATS_WIDTH}
      />
    );
  }

  return (
    <Grid
      p={1}
      flexDirection="column"
      sx={bottomBorderSx}
      width={WEEK_STATS_WIDTH}
    >
      <Typography variant="body2" fontWeight="600">
        {weeklyAvailable.available} {formatMessage(messages.availableLabel)}
      </Typography>

      <Stack
        bgcolor={colors.lightRed}
        borderRadius={theme.spacing(1)}
        sx={stackSx}
      >
        <Typography variant="caption" color={colors.maroon} fontWeight="600">
          {weeklyAvailable.unavailable}{' '}
          {formatMessage(messages.unpaidLeaveLabel)}
        </Typography>
      </Stack>
    </Grid>
  );
};

interface JobBadgeProps {
  label: string;
  filled: number;
  count: number;
}

const JobBadge = ({ label, count, filled }: JobBadgeProps) => {
  const remainingSlots = count - filled;
  const theme = useTheme();

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      bgcolor={colors.lighterBrown}
      borderRadius={theme.spacing(1)}
      sx={stackSx}
    >
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2" color={colors.lightBrown}>
        {filled}/{count}
      </Typography>

      {remainingSlots > 0 && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          ml={theme.spacing(0.5)}
          bgcolor={colors.orangeRed}
          width={theme.spacing(1.5)}
          height={theme.spacing(1.5)}
          borderRadius={theme.spacing(1)}
        >
          <Typography fontSize={theme.spacing(1)} color="common.white">
            {remainingSlots}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

interface Props {
  monthIndex: number;
  months: string[];
  data?: Record<string, UCM.CalendarJobType>;
  onReject: (data: UCM.CalendarJobType) => void;
  onApprove: (data: UCM.CalendarJobType) => void;
  onDayPress: (day: string | null) => void;
  weeklyUsersAvailability?: Record<number, WeeklyStats>;
}

export default function JobCalendarDays({
  monthIndex,
  months,
  data,
  onReject,
  onApprove,
  onDayPress,
  weeklyUsersAvailability,
}: Props) {
  const { formatMessage } = useIntl();

  const [selected, setSelected] = useState<string | null>(null);

  const handlePressReject = useCallback(
    (event: React.MouseEvent<HTMLElement>, data: UCM.CalendarJobType) => {
      event.stopPropagation();
      onReject(data);
    },
    [onReject],
  );

  const handlePressApprove = useCallback(
    (event: React.MouseEvent<HTMLElement>, data: UCM.CalendarJobType) => {
      event.stopPropagation();
      onApprove(data);
    },
    [onApprove],
  );

  const handlePressDay = useCallback(
    (date: string) => {
      const day = selected !== date ? date : null;

      onDayPress(day);
      setSelected(day);
    },
    [onDayPress, selected],
  );

  const days = useMemo(() => {
    const month = months[monthIndex];
    if (!month) return null;

    const currMonth = toMoment(month)?.month();
    const lastDay = toMoment(month)?.endOf('month').endOf('week');
    const currDay = toMoment(month)?.startOf('month').startOf('week');

    let currWeek = null;
    let dates: Array<JSX.Element> = [];
    const weeks: Array<JSX.Element> = [];

    while (currDay && lastDay && currDay < lastDay) {
      if (currWeek !== currDay.week()) {
        if (dates.length > 0 && currWeek) {
          weeks.push(
            <Grid key={currWeek} container size={12}>
              {dates}
              <WeekStats
                week={currWeek}
                weeklyUsersAvailability={weeklyUsersAvailability}
              />
            </Grid>,
          );
        }
        dates = [];
        currWeek = currDay.week();
      }

      const disabledDay = currDay.month() !== currMonth;
      const dayColor = disabledDay ? 'textDisabled' : 'primary';
      const currDayString = currDay?.format(moment.HTML5_FMT.DATE);
      const currDayData = data?.[currDayString];

      const isWaitlisted = Boolean(
        currDayData?.isWaitlisted && currDayData.status === 'accepted',
      );

      const bgColor = (theme: DefaultTheme) =>
        getJobStatusColor(currDayData, isWaitlisted, theme);

      dates.push(
        <Grid
          key={currDayString}
          flex={1}
          px={0.5}
          height={DAY_CELL_HEIGHT}
          onClick={() => handlePressDay(currDayString)}
          sx={(theme) => ({
            pointerEvents: disabledDay ? 'none' : 'pointer',
            borderBottom: `thin solid ${theme.palette.divider}`,
            borderRight: `thin solid ${theme.palette.divider}`,
            backgroundColor:
              currDayString === selected ? colors.lighterBlue : bgColor(theme),
          })}
          position="relative"
        >
          <Typography variant="body2" color={dayColor} mb={0.5}>
            {currDay.format('DD')}
          </Typography>

          <Typography fontWeight="600" fontSize={11} textTransform="capitalize">
            {isWaitlisted
              ? formatMessage(messages.waitlistedLabel)
              : currDayData?.status}
          </Typography>

          {currDayData && (
            <Box>
              <JobBadge
                label="M"
                count={currDayData.slotsNumber}
                filled={currDayData.bookedCount}
              />
            </Box>
          )}
          {currDayData && (
            <DayActionButtons
              currDayData={currDayData}
              onReject={handlePressReject}
              onApprove={handlePressApprove}
            />
          )}
        </Grid>,
      );
      currDay.add(1, 'day');
    }

    if (dates.length > 0 && currWeek) {
      weeks.push(
        <Grid key={currWeek} container size={12}>
          {dates}
          <WeekStats
            week={currWeek}
            weeklyUsersAvailability={weeklyUsersAvailability}
          />
        </Grid>,
      );
    }
    return weeks;
  }, [data, months, monthIndex, selected, weeklyUsersAvailability]);

  return <>{days}</>;
}
